import { escapeHtml } from '@colplat/ui-web';
import { apiFetch } from '../api.js';
import { globals } from '../globals.js';
import { storeSession } from '../auth-storage.js';
import { placeToResume } from '../lastPlace.js';

/**
 * Plain, non-collaborative — no lease/history/presence chrome anywhere in
 * this flow (ADR-0018). Every failure — unknown username, wrong password,
 * disabled, locked, IP-blocked — looks identical here: the "checking" state
 * just clears and the page stays put. No specific message is ever shown.
 */
export function mountLogin(root: HTMLElement): void {
  root.innerHTML = `
    <div class="container" style="max-width:400px;margin-top:12vh">
      <div class="cf-card p-4">
        <h1 class="h4 mb-3">Sign in</h1>
        <form id="loginForm" novalidate autocomplete="on">
          <div class="mb-3">
            <label class="form-label" for="loginUsername">Username</label>
            <input class="form-control" id="loginUsername" name="username" autocomplete="username" required>
          </div>
          <div class="mb-3">
            <label class="form-label" for="loginPassword">Password</label>
            <input class="form-control" id="loginPassword" name="password" type="password"
              autocomplete="current-password" required>
          </div>
          <button class="btn btn-primary w-100" type="submit" id="loginSubmit">Sign in</button>
          <div class="mt-2" style="font-size:.8rem;min-height:1.2em" id="loginStatus" aria-live="polite"></div>
        </form>
      </div>
      <div id="testLoginPanel"></div>
    </div>`;

  mountTestLoginPanel(root);

  const form = root.querySelector<HTMLFormElement>('#loginForm')!;
  const status = root.querySelector<HTMLElement>('#loginStatus')!;
  const submitBtn = root.querySelector<HTMLButtonElement>('#loginSubmit')!;
  const usernameInput = root.querySelector<HTMLInputElement>('#loginUsername')!;
  const passwordInput = root.querySelector<HTMLInputElement>('#loginPassword')!;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void submit();
  });

  async function submit(): Promise<void> {
    submitBtn.disabled = true;
    status.textContent = 'Checking…';

    try {
      const res = await apiFetch('/auth/login', {
        method: 'POST',
        body: { username: usernameInput.value, password: passwordInput.value },
      });
      if (res.ok) {
        const data = (await res.json()) as { token: string; userId: string };
        storeSession(data);
        // Back where this person left off on this device, or the apps list
        // when there is nothing to go back to.
        window.location.assign(placeToResume(data.userId));
        return;
      }
    } catch {
      // fall through — the same silent failure as any other rejected login
    }
    status.textContent = '';
    submitBtn.disabled = false;
  }
}

/**
 * Passwordless one-click sign-in, rendered only when the server told us it
 * registered the route (`globals().testLogin`, injected by services/api's
 * static step). The client never assumes it: on a production build the flag
 * is absent, this returns immediately, and the panel does not exist in the
 * DOM at all.
 *
 * The point is being able to *be two people at once* — leases, presence, the
 * ownership marker and take-over are only meaningfully testable that way, and
 * making that need several real passwords was quietly making the
 * collaborative paths the least-exercised part of the app.
 *
 * Loud on purpose: this is an auth bypass, and a test environment that looks
 * exactly like production while accepting no password is worse than one that
 * says so.
 */
function mountTestLoginPanel(root: HTMLElement): void {
  if (!globals().testLogin) return;
  const panel = root.querySelector<HTMLElement>('#testLoginPanel')!;
  panel.innerHTML = `
    <div class="cf-card p-3 mt-3" style="border-color:#b45309">
      <div class="d-flex align-items-center gap-2 mb-2">
        <span class="badge text-bg-warning">TEST ENVIRONMENT</span>
        <span class="fw-semibold" style="font-size:.85rem">Sign in without a password</span>
      </div>
      <p class="text-secondary mb-2" style="font-size:.75rem">
        Enabled by <code>COLPLAT_TEST_LOGIN=1</code>. Never available in production.
      </p>
      <div id="testLoginUsers" class="d-grid gap-1" aria-live="polite">
        <span class="text-secondary" style="font-size:.8rem">Loading users…</span>
      </div>
    </div>`;

  void (async () => {
    const list = panel.querySelector<HTMLElement>('#testLoginUsers')!;
    let users: TestUser[];
    try {
      const res = await apiFetch('/auth/test-users');
      if (!res.ok) throw new Error('unavailable');
      users = ((await res.json()) as { users: TestUser[] }).users;
    } catch {
      list.textContent = 'Could not load the user list.';
      return;
    }
    list.innerHTML = '';
    for (const user of users) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn-sm btn-outline-secondary text-start';
      btn.disabled = user.disabled;
      btn.innerHTML =
        `<strong>${escapeHtml(user.displayName)}</strong> ` +
        `<span class="text-secondary">${escapeHtml(user.username)}</span>` +
        (user.roles.length > 0
          ? `<div class="text-secondary" style="font-size:.7rem">${escapeHtml(user.roles.join(' · '))}</div>`
          : '') +
        (user.disabled ? '<div class="text-secondary" style="font-size:.7rem">disabled</div>' : '');
      btn.addEventListener('click', () => { void signInAs(user.username, list); });
      list.append(btn);
    }
  })();
}

interface TestUser {
  userId: string; username: string; displayName: string; roles: string[]; disabled: boolean;
}

async function signInAs(username: string, list: HTMLElement): Promise<void> {
  const res = await apiFetch('/auth/test-login', { method: 'POST', body: { username } });
  if (!res.ok) {
    list.setAttribute('data-error', 'true');
    return;
  }
  const session = (await res.json()) as { token: string; userId: string };
  storeSession(session);
  window.location.assign(placeToResume(session.userId));
}
